import FileBrowser, {
  UseLoadFile,
  UseLoadFileUrl,
} from '@wandb/weave/common/components/FileBrowser';
import React, {useCallback, useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {useRunFilesQuery} from '../generated/graphql';
import {File} from '../state/graphql/runFilesQuery';
import {useSingleFileQuery} from '../state/graphql/singleFileQuery';
import {MAX_NUM_FILES} from '../util/constants';
import {trackPageViewed} from '../util/navigation';
import {useLoadFile} from '../util/requests';
import * as Urls from '../util/urls';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';

interface RunFilesProps {
  totalFiles: number;
  isCodeHidden?: boolean;
  entityName: string;
  projectName: string;
  runName: string;
  path?: string;
  history: any;
  groupName?: string;
  sweepName?: string;
  setSelectedRunName?: (name: string) => void;
}

const RunFiles = (props: RunFilesProps) => {
  // for page view tracking
  useEffect(() => {
    let pageName = 'Run';
    if (props.groupName != null) {
      pageName = 'Group run';
    } else if (props.sweepName != null) {
      pageName = 'Sweep run';
    }
    trackPageViewed(pageName, 'Files');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    entityName,
    projectName,
    runName,
    history,
    path,
    groupName,
    setSelectedRunName = () => null,
  } = props;

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRunName = params.get('runName');
    if (urlRunName) {
      setSelectedRunName(urlRunName);
    }
  }, [setSelectedRunName, location.search]);

  const setFilePath = useCallback(
    (newPathArray: string[]) => {
      history.push(
        groupName
          ? Urls.runGroup({
              entityName,
              projectName,
              name: groupName,
              tab: 'files',
              filePath: newPathArray,
              runName,
            })
          : Urls.runTabFile(entityName, projectName, runName, newPathArray)
      );
    },
    [entityName, projectName, runName, history, groupName]
  );
  const runFilesQuery = useRunFilesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entityName,
      projectName,
      runName,
      maxNumFiles: MAX_NUM_FILES,
    },
  });

  if (runFilesQuery.loading) {
    return <Loader name="run-files-loader" size="huge" />;
  }
  const files =
    runFilesQuery.data?.project?.run?.files?.edges.map(e => e.node as File) ??
    [];

  const useLoadRunFile: UseLoadFile = (file, options) => {
    return useLoadFile({entityName, projectName, runName}, file.name, options);
  };
  const useLoadRunFileUrl: UseLoadFileUrl = file => {
    return useSingleFileQuery({
      entityName,
      projectName,
      runName,
      fileName: file.name,
    });
  };
  return (
    <FileBrowser
      totalFiles={props.totalFiles}
      isCodeHidden={props.isCodeHidden}
      files={files}
      path={path}
      history={history}
      useLoadFile={useLoadRunFile}
      useLoadFileUrl={useLoadRunFileUrl}
      setFilePath={setFilePath}
    />
  );
};

export default RunFiles;
